/** Plans d’abonnement, CRM, budget — types front partagés. */

/** Essai gratuit à l’inscription (jours). */
export const TRIAL_DAYS = 14;

/** Inactivité au-delà de laquelle le compte est purgé (mois). */
export const INACTIVITY_PURGE_MONTHS = 12;

/** Délai entre échéances d’abonnement fractionné (jours). */
export const INSTALLMENT_INTERVAL_DAYS = 28;

/** Seuil d’alerte avant fin d’essai ou d’abonnement (jours). */
export const SUBSCRIPTION_EXPIRY_ALERT_DAYS = 7;

export const SUBSCRIPTION_PLANS = [
  {
    key: 'monthly',
    label: 'Mensuel',
    labelEn: 'Monthly',
    price: 35,
    currency: 'EUR',
    durationDays: 30,
    installmentCount: 1,
    installmentAmounts: [35],
    description: 'Payable en une seule fois. Accès complet pendant 30 jours.',
    descriptionEn: 'Paid in one installment. Full access for 30 days.',
  },
  {
    key: 'semestrial',
    label: 'Semestriel',
    labelEn: 'Semi-annual',
    price: 160,
    currency: 'EUR',
    durationDays: 182,
    installmentCount: 2,
    installmentAmounts: [80, 80],
    description: 'Deux échéances de 80 € (initial + 28 jours).',
    descriptionEn: 'Two installments of €80 (initial + after 28 days).',
  },
  {
    key: 'annual',
    label: 'Annuel',
    labelEn: 'Annual',
    price: 250,
    currency: 'EUR',
    durationDays: 365,
    installmentCount: 3,
    /** 84 + 83 + 83 = 250 € (CDC : trois modalités après 28 j). */
    installmentAmounts: [84, 83, 83],
    description: 'Trois échéances (84 € puis 83 € × 2 après 28 jours).',
    descriptionEn: 'Three installments (€84 then €83 × 2 after 28 days).',
  },
] as const;

export type SubscriptionPlanKey = (typeof SUBSCRIPTION_PLANS)[number]['key'];

export function getSubscriptionPlan(key: string) {
  return SUBSCRIPTION_PLANS.find((p) => p.key === key) ?? null;
}

export function formatEuro(amount: number, locale: 'fr' | 'en' = 'fr') {
  return new Intl.NumberFormat(locale === 'en' ? 'en-EU' : 'fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Statuts stockés en base (crm_leads.status). */
export const CRM_STATUSES = [
  { key: 'nouveau', label: 'Nouveau' },
  { key: 'contacte', label: 'Contacté' },
  { key: 'negociation', label: 'Négociation' },
  { key: 'gagne', label: 'Gagné' },
  { key: 'perdu', label: 'Perdu' },
] as const;

export type CrmStatusKey = (typeof CRM_STATUSES)[number]['key'];

/**
 * Pipeline de conversion en mode processus (réf. CRM Pro).
 * L’étape « Résultats » regroupe gagné + perdu.
 */
export const CRM_PIPELINE_STAGES = [
  {
    key: 'nouveau',
    step: 1,
    label: 'Nouveau',
    description: 'nouvelle expression de besoin',
    tone: 'grey',
    statuses: ['nouveau'] as const,
  },
  {
    key: 'contacte',
    step: 2,
    label: 'Contacté',
    description: 'relation établie avec le prospect',
    tone: 'blue',
    statuses: ['contacte'] as const,
  },
  {
    key: 'negociation',
    step: 3,
    label: 'Négociation',
    description: 'discussion pour accord',
    tone: 'purple',
    statuses: ['negociation'] as const,
  },
  {
    key: 'resultats',
    step: 4,
    label: 'Résultats',
    description: 'gagné / perdu',
    tone: 'green',
    statuses: ['gagne', 'perdu'] as const,
  },
] as const;

/** Normalise d’anciens statuts éventuels vers le pipeline actuel. */
export function normalizeCrmStatus(status: string): CrmStatusKey {
  if (status === 'discussion') return 'contacte';
  if (status === 'proposition') return 'negociation';
  if ((CRM_STATUSES as readonly { key: string }[]).some((s) => s.key === status)) {
    return status as CrmStatusKey;
  }
  return 'nouveau';
}

export const BUDGET_CATEGORIES = [
  'Investissement',
  'Marketing',
  'Opérations',
  'RH',
  'Juridique',
  'Technologie',
  'Autres',
] as const;

export type SwotItem = {
  element: string;
  proposition_ia: string;
  recommandation: string;
};

export type SwotData = {
  strengths: SwotItem[];
  weaknesses: SwotItem[];
  opportunities: SwotItem[];
  threats: SwotItem[];
};

/** Piliers CDC — Matrice stratégique */
export const STRATEGIC_PILLARS = [
  { code: 'orientation', pilier: 'Orientation stratégique', ordre: 1 },
  { code: 'offre', pilier: 'Offre de valeur', ordre: 2 },
  { code: 'commercial', pilier: 'Développement commercial', ordre: 3 },
  { code: 'operations', pilier: 'Organisation et opérations', ordre: 4 },
  { code: 'finance', pilier: 'Performance financière', ordre: 5 },
  { code: 'innovation', pilier: 'Innovation et développement', ordre: 6 },
] as const;

export type StrategicMatrixRow = {
  code: string;
  pilier: string;
  ordre: number;
  diagnostic: string;
  intention: string;
  objectifs_smart: string;
  actions_prioritaires: string;
  indicateurs_kpi: string;
};

/** @deprecated Ancien format — préférer StrategicMatrixRow */
export type MatrixAxis = {
  code: string;
  titre: string;
  ordre: number;
  objectifs_strategiques: string[];
  resultats_attendus: string[];
  indicateurs_cles_kpi: string[];
  responsables: string[];
};

export function emptySwot(): SwotData {
  return { strengths: [], weaknesses: [], opportunities: [], threats: [] };
}

export function emptySwotItem(): SwotItem {
  return { element: '', proposition_ia: '', recommandation: '' };
}

export function emptyStrategicMatrix(): StrategicMatrixRow[] {
  return STRATEGIC_PILLARS.map((p) => ({
    code: p.code,
    pilier: p.pilier,
    ordre: p.ordre,
    diagnostic: '',
    intention: '',
    objectifs_smart: '',
    actions_prioritaires: '',
    indicateurs_kpi: '',
  }));
}

export function normalizeStrategicMatrix(raw: unknown): StrategicMatrixRow[] {
  const base = emptyStrategicMatrix();
  if (!Array.isArray(raw) || raw.length === 0) return base;
  const first = raw[0] as Record<string, unknown>;
  // Ancien format (axes avec objectifs_strategiques[])
  if (first && 'objectifs_strategiques' in first && !('diagnostic' in first)) {
    return base;
  }
  return base.map((row) => {
    const found = (raw as StrategicMatrixRow[]).find(
      (r) => r.code === row.code || r.pilier === row.pilier || r.ordre === row.ordre,
    );
    if (!found) return row;
    return {
      ...row,
      diagnostic: found.diagnostic ?? '',
      intention: found.intention ?? '',
      objectifs_smart: found.objectifs_smart ?? '',
      actions_prioritaires: found.actions_prioritaires ?? '',
      indicateurs_kpi: found.indicateurs_kpi ?? '',
    };
  });
}
